import { createFileRoute, Link } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { GlossaryTerms } from "@/components/calv/GlossaryTerms";
import { LogoReferences } from "@/components/calv/LogoReferences";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { ProcessFlow } from "@/components/calv/ProcessFlow";
import { Capabilities } from "@/components/calv/Capabilities";
import { StackGrid } from "@/components/calv/StackGrid";
import { Callout } from "@/components/calv/Callout";
import { CTABlock } from "@/components/calv/CTABlock";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { RelatedServices } from "@/components/calv/RelatedServices";
import { relatedBySource } from "@/lib/related-services-data";

export const Route = createFileRoute("/leistungen/seo-ai-seo")({
  head: () => {
    const h = pageHead("seoAiSeo", "de");
    return { meta: h.meta, links: h.links };
  },
  component: SeoPage,
});

const steps = [
  { num: "01", title: "Analyse von Sichtbarkeit, Nachfrage und wirtschaftlicher Wirkung", body: "Wir analysieren nicht nur Rankings, sondern vor allem deren tatsächlichen Beitrag zum Geschäft. Welche Suchanfragen kauf- oder kontaktbereit sind, welche Seiten messbar zu Umsatz beitragen, wo organischer Traffic konvertiert. Ziel ist es, SEO-Potenziale mit echtem wirtschaftlichem Hebel zu identifizieren." },
  { num: "02", title: "Wirtschaftliche Priorisierung statt SEO-Aktionismus", body: "Nicht jede SEO-Maßnahme ist sinnvoll – und viele sind wirtschaftlich irrelevant. Wir priorisieren nach erwartetem Einfluss auf Umsatz, Suchintention, Umsetzbarkeit und Skalierbarkeit. So entsteht ein SEO-Fahrplan, der nicht theoretisch korrekt, sondern wirtschaftlich sinnvoll ist." },
  { num: "03", title: "Struktur, Inhalte & Suchintention sauber verzahnen", body: "SEO wirkt dann am besten, wenn Struktur und Inhalte exakt zur Such- und Entscheidungssituation des Nutzers passen. Wir optimieren Seiten- und Kategoriestrukturen, Content-Strukturen und interne Verlinkung. Es geht nicht um mehr Content, sondern um den richtigen Content an der richtigen Stelle." },
  { num: "04", title: "SEO, SEA und Conversion als zusammenhängendes System", body: "SEO entfaltet seinen vollen Wert im Zusammenspiel mit anderen Kanälen. SEA zeigt früh, welche Suchbegriffe konvertieren. SEO übernimmt diese Themen nachhaltig. So reduziert SEO langfristig die Abhängigkeit von steigenden Klickpreisen und stabilisiert das Gesamtsystem." },
];

const capabilities = [
  { name: "Technisches SEO", detail: "Crawlbarkeit, Indexierung, Core Web Vitals" },
  { name: "Content-Strukturen", detail: "Argumentationslogik, Suchintention" },
  { name: "Seiten- & Kategoriestruktur", detail: "Hierarchien, interne Verlinkung" },
  { name: "Leistungs- & Produktseiten", detail: "Conversion-orientiert" },
  { name: "AI-SEO", detail: "KI-gestützte Analyse, Themenräume" },
  { name: "Wettbewerbsanalyse", detail: "Marktstruktur, Lückenanalyse" },
  { name: "Priorisierung", detail: "wirtschaftlicher Hebel statt Ranking" },
  { name: "Verzahnung mit SEA & CRO", detail: "als zusammenhängendes System" },
];

const stack = [
  { name: "Google Search Console", small: "Performance, Indexing" },
  { name: "Google Analytics (GA4)", small: "Verhalten, Conversions" },
  { name: "Screaming Frog", small: "Technische SEO-Audits" },
  { name: "Ahrefs", small: "Backlinks, Keywords" },
  { name: "Sistrix", small: "Sichtbarkeit, Wettbewerb" },
  { name: "Matomo", small: "Datenschutzkonforme Analyse" },
  { name: "Seobility", small: "OnPage-Analyse" },
  { name: "AI-Analyse-Workflows", small: "Themenraum-Strukturierung" },
];

function SeoPage() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          eyebrow="SEO & AI SEO"
          headline={<>SEO ist <em>kein Reichweitenziel,</em> sondern ein wirtschaftlicher Hebel.</>}
          lead="Klassisches SEO ist nur noch der halbe Weg. Wir optimieren parallel für Google, ChatGPT, Perplexity und Claude — denn Suche findet zunehmend in KI statt, nicht in Suchergebnislisten."
          breadcrumbs={[{ label: "Start", to: "/" }, { label: "Leistungen", to: "/leistungen" }, { label: "SEO & AI SEO" }]}
        />

        <Section number="02" kicker="Standpunkt" headline={<>Sichtbarkeit ist nur dann wertvoll, wenn sie <em>wirtschaftlich wirkt.</em></>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">Viele Unternehmen investieren über Jahre in SEO und erreichen Sichtbarkeit – ohne dass sich diese spürbar im Geschäftsergebnis widerspiegelt. Der Grund ist selten fehlende Arbeit. Der Grund ist fast immer eine falsche Priorisierung.</p>
              <p className="mb-5">SEO bedeutet für uns nicht, möglichst viele Keywords zu besetzen. Es bedeutet, Suchanfragen mit echter geschäftlicher Relevanz zu identifizieren und systematisch so zu bedienen, dass daraus messbare Ergebnisse entstehen. Sichtbarkeit ist nur dann wertvoll, wenn sie wirtschaftlich wirkt.</p>
              <p className="mb-5">Unser SEO-Ansatz ist nicht isoliert, sondern immer Teil eines Gesamtsystems aus Nachfrage, Conversion und Wirtschaftlichkeit. Wir betrachten SEO nicht aus Ranking-Perspektive, sondern aus Geschäfts-Perspektive.</p>
              <p className="mb-5">Wo Mandanten eigene Content-Kapazität haben, beraten wir bei der Content-Strategie und liefern Briefings, Themenpläne und Optimierungs-Empfehlungen — die Texte schreiben dann die internen Teams. Wo diese Kapazität fehlt, erstellen wir Content selbst, auf Basis intensiver Keyword-Recherchen, Search-Intent-Analyse und Wettbewerbs-Lückenanalyse. Die Bandbreite reicht von Long-Form-Ratgebern und Pillar-Pages über conversion-orientierte Service- und Kategorie-Texte bis hin zu KI-getriebenen Content-Pipelines für programmatic SEO, wo Volumen strategisch nötig ist. In allen Fällen gilt: Content ist kein Selbstzweck — Texte, die zwar ranken, aber nicht in Conversion-Pfade eingebunden sind, sind verbranntes Aufwandsbudget. Wir verzahnen Content-Produktion immer mit der Performance-Logik des gesamten Setups.</p>
              <p>Für Mandate mit regionalem Schwerpunkt führen wir diese Methodik als eigenständige Disziplin: Unsere{" "}
                <Link to="/regionen/wuerzburg/seo-agentur" className="text-cyan-deep underline-offset-4 hover:underline">SEO-Agentur in Würzburg</Link>{" "}
                verbindet wirtschaftlich priorisierte Suchmaschinenoptimierung mit der Nähe zum unterfränkischen Mittelstand — klassisches SEO plus Sichtbarkeit in Google, ChatGPT, Perplexity und Claude.</p>
            </div>
          </RevealOnScroll>
          <Callout>Nicht jede Sichtbarkeit ist wertvoll. Wir investieren nur dort, wo SEO messbar zum Geschäft beiträgt.</Callout>
        </Section>

        <Section variant="tinted" number="03" kicker="Vorgehen" headline={<>Vier Phasen, die SEO <em>wirtschaftlich</em> machen.</>}>
          <ProcessFlow steps={steps} />
          <RevealOnScroll>
            <div className="mt-12 rounded-lg bg-white p-6 md:p-10">
              <div className="grid gap-8 md:grid-cols-[minmax(0,_2fr)_minmax(0,_3fr)] md:gap-14">
                <div>
                  <p className="eyebrow mb-3">Optionaler Baustein</p>
                  <h3 className="font-display text-xl text-ink">Content-Erstellung im Mandate-Mix</h3>
                </div>
                <p className="text-[17px] leading-relaxed text-ink-2">
                  Wo Mandanten diese Kapazität bei uns abrufen, integrieren wir die Content-Produktion in die Phasen 02 bis 04. Konkret heißt das: Keyword-Cluster und Content-Briefings entstehen in Phase 02 (Setup), erste Content-Stücke gehen ab Phase 03 (Skalierung) live, und in Phase 04 (Betrieb) läuft ein kontinuierlicher Content-Iterations-Rhythmus. Volumen-Bedarfe ab 20–30 Stücken pro Monat lösen wir mit KI-gestützten Content-Pipelines plus menschlicher Redaktion und Markentonalitäts-Disziplin — nicht mit reinen AI-Outputs ohne Editorial-Hand.
                </p>
              </div>
            </div>
          </RevealOnScroll>
          <Callout>SEO ist kein isolierter Kanal. Es ist ein Stabilitätsfaktor im gesamten Performance-System.</Callout>
        </Section>

        <Section number="04" kicker="Capabilities & Stack" headline={<>Womit wir SEO <em>steuerbar</em> machen.</>}>
          <Capabilities items={capabilities} />
          <RevealOnScroll>
            <p className="mt-8 max-w-2xl text-[15px] leading-relaxed text-ink-3">
              AI-SEO führen wir bei uns als eigene Disziplin —{" "}
              <Link
                to="/leistungen/geo-generative-engine-optimization"
                className="text-cyan-deep underline-offset-4 hover:underline"
              >
                Generative Engine Optimization (GEO)
              </Link>
              . Sie baut auf derselben technischen Basis auf und entscheidet, ob KI-Systeme Sie als Quelle nennen.
            </p>
          </RevealOnScroll>
          <div className="mt-12">
            <p className="eyebrow mb-5">Stack</p>
            <StackGrid items={stack} />
          </div>
        </Section>

        <LogoReferences locale="de" number="05" />

        <RelatedServices
          locale="de"
          fromId="seo-ai-seo"
          number="06"
          items={relatedBySource["seo-ai-seo"]}
        />

        <Section kicker="Region" headline={<>SEO für eine <em>konkrete Region.</em></>}>
          <div className="grid gap-4 md:grid-cols-2">
            <Link
              to="/regionen/wuerzburg/seo-agentur"
              className="group block rounded-lg border border-rule bg-white p-6 transition-colors hover:border-cyan-deep/50"
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-4">Region</div>
              <div className="mt-2 font-display text-lg leading-snug text-ink">SEO-Agentur Würzburg</div>
              <p className="mt-3 leading-relaxed text-ink-3">Unsere SEO-Leistung für den Wirtschaftsraum Würzburg und Unterfranken.</p>
              <div className="mt-4 font-mono text-xs uppercase tracking-wider text-cyan-deep group-hover:underline">Mehr erfahren ↗</div>
            </Link>
          </div>
        </Section>


        <Section number="07" kicker="Engagement">
          <CTABlock
            headline={<>Ist Ihr SEO <em>wirtschaftlich priorisiert?</em></>}
            body="Sie möchten wissen, welche organischen Themen Ihrem Geschäft wirklich etwas bringen – und welche nicht?"
            primary={{ label: "Kontakt aufnehmen", to: "/kontakt" }}
            secondary={{ label: "Alle Leistungen", to: "/leistungen" }}
          />
        </Section>
        <GlossaryTerms locale="de" ids={["ai-overviews", "ai-mode", "geo", "pillar-content", "snippet-suitability", "eeat"]} />
      </main>
      <Footer />
    </>
  );
}
